import { mm2ToFt2, mm2ToM2, mmToImperialPattern, mmToMetricPattern } from './number-converter';

export const DecimalSeparator = Object.freeze({
  DOT: Symbol('dot'),
  COMMA: Symbol('comma'),
});

export const UnitSystem = Object.freeze({
  IMPERIAL: Symbol('imperial'),
  METRIC: Symbol('metric'),
});

export default class NumberFormatter {
  constructor(options) {
    ({
      decimalSeparator: this.decimalSeparator = window.decimalSeparator === 'dot' ? DecimalSeparator.DOT : DecimalSeparator.COMMA,
      unitSystem: this.unitSystem = window.unitSystem === 'imperial' ? UnitSystem.IMPERIAL : UnitSystem.METRIC,
      thousandsSeparator: this.thousandsSeparator = window.thousandsSeparator ?? '\u2009',
    } = options);
  }

  formatDecimal(value) {
    return this.decimalSeparator === DecimalSeparator.COMMA
      ? this._formatDecimalComma(value)
      : this._formateDecimalDot(value);
  }

  formatDecimalWithThousandsSeparator(value, separator) {
    let formatted = this.formatDecimal(value);
    formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, separator ?? this.thousandsSeparator);
    return formatted;
  }

  formatFloat(value) {
    return Number.parseFloat(this._formateDecimalDot(value));
  }

  formatImperial(feetInchFraction) {
    let formatted = '';
    if (feetInchFraction.feet > 0) {
      formatted += `${feetInchFraction.feet}'`;
    }

    if (feetInchFraction.inch > 0) {
      if (formatted.length) {
        formatted += ' ';
      }
      formatted += `${feetInchFraction.inch}"`;
    }

    if (feetInchFraction.fraction.numerator > 0) {
      if (formatted.length) {
        formatted += ' ';
      } else {
        formatted += '0" ';
      }
      formatted += `${feetInchFraction.fraction.numerator}/${feetInchFraction.fraction.denominator}`;
    }

    if (formatted.length && feetInchFraction.sign) {
      formatted = `${feetInchFraction.sign}${formatted}`;
    }

    if (!formatted.length) {
      formatted = '0';
    }

    return formatted;
  }

  formatMetric(metricValues, withUnit = true) {
    var formatted = '';
    if (metricValues.m > 0) {
      formatted += `${metricValues.m}`;
      formatted += withUnit ? ' m' : '';
    }

    if (metricValues.cm > 0) {
      formatted += `${metricValues.cm}`;
      formatted += withUnit ? ' cm' : '';
    }

    if (metricValues.mm > 0) {
      formatted += `${metricValues.mm}`;
      formatted += withUnit ? ' mm' : '';
    }

    if (formatted.length && metricValues.sign) {
      formatted = `${metricValues.sign}${formatted}`;
    }

    if (!formatted.length) {
      formatted = '0';
    }

    return formatted;
  }

  isImperial() {
    return this.unitSystem === UnitSystem.IMPERIAL;
  }

  formatOutput(valueMm, formatPattern, withUnit = true) {
    if (this.unitSystem === UnitSystem.IMPERIAL) {
      return this.formatImperial(mmToImperialPattern(valueMm, formatPattern));
    } else {
      return this.formatMetric(mmToMetricPattern(valueMm, formatPattern), withUnit);
    }
  }

  formatSquareOutput(valueMm2, formatPattern) {
    switch (formatPattern) {
      case 'm2':
        return `${this.formatDecimal(parseFloat(mm2ToM2(valueMm2)).toFixed(2))} m<sup>2</sup>`;
      case 'ft2':
        return `${parseInt(mm2ToFt2(valueMm2))} ft<sup>2</sup>`;
      case 'mm2':
      default:
        return `${parseInt(valueMm2)} mm<sup>2</sup>`;
    }
  }

  formatRangeOutput(valueMmA, valueMmB, formatPattern) {
    var result = '';
    if (this.unitSystem === UnitSystem.IMPERIAL) {
      result = this.formatImperial(mmToImperialPattern(valueMmA, formatPattern));
      if (valueMmA !== valueMmB) {
        result += ' - ';
        result += this.formatImperial(mmToImperialPattern(valueMmB, formatPattern));
      }
    } else {
      if (valueMmA !== valueMmB) {
        result += this.formatMetric(mmToMetricPattern(valueMmA, formatPattern), false);
        result += ' - ';
        result += this.formatMetric(mmToMetricPattern(valueMmB, formatPattern));
      } else {
        result += this.formatMetric(mmToMetricPattern(valueMmA, formatPattern));
      }
    }

    return result;
  }

  _formatDecimalComma(value) {
    let formatted = value;
    formatted = formatted.replace(/[\.]/g, ',');
    formatted = formatted.replace(/[^0-9\,]/g, '');
    formatted = formatted.split(',').length > 2 ? formatted.replace(/\,+$/, '') : formatted;
    return formatted;
  }

  _formateDecimalDot(value) {
    let formatted = value;
    formatted = formatted.replace(/[\,]/g, '.');
    formatted = formatted.replace(/[^0-9\.]/g, '');
    formatted = formatted.split('.').length > 2 ? formatted.replace(/\.+$/, '') : formatted;
    return formatted;
  }
}
